<template>
  <base-layout manualRedirect @go-back="goBack">
    <v-container class="sms-recover" fluid>
      <v-row no-gutters>
        <v-col>
          <p class="sms-recover-title" :style="{ color: getAssets.colors.form.buttonBackground }">Você já faz parte do clube!</p>
          <p v-if="showPinScreen" class="sms-recover-subtitle">
            Estamos quase lá! Você deve receber por SMS um código de verificação no número
            <strong :style="{ color: getAssets.colors.form.buttonBackground }">{{ phoneConfirm }}</strong>
          </p>
          <p v-else class="sms-recover-subtitle">
            Para criarmos uma nova senha precisamos confirmar este dado: <span class="sms-recover-break">Digite seu número de telefone completo.</span>
          </p>
          <p v-if="showPinScreen" class="sms-recover-subtitle">Digite o código nos campos abaixo</p>
        </v-col>
      </v-row>

      <password-recover-sms-pin
        v-if="showPinScreen"
        :btnLoading="btnLoading"
        :error="error"
        :pinMessage="pinMessage"
        :buttonText="buttonText"
        :secondsText="secondsText"
        :timer="timer"
        @remove-error="error = false"
        @send-code="handlePin($event)"
        @submit="submitPhoneNumber"
      />

      <password-recover-sms-fields
        v-else
        :hiddenPhone="hiddenPhone"
        label="CPF/CNPJ"
        :cpfCnpj="cpfCnpj"
        :phoneConfirm="phoneConfirm"
        :phoneConfirmError="phoneConfirmError"
        :btnLoading="btnLoading"
        @remove-error="phoneConfirmError = ''"
        @set-cpf-cnpj="cpfCnpj = $event"
        @set-phone-confirm="phoneConfirm = $event"
        @submit="submitPhoneNumber"
      />

      <v-row no-gutters :class="{ 'sms-recover-pin-screen': showPinScreen }">
        <span class="link" @click="$router.push({ path: '/contato' })">Não posso ou não quero usar esse número</span>
      </v-row>
    </v-container>
  </base-layout>
</template>

<script>
import { sanitizeCnpj } from '@/helpers/cnpj'

export default {
  components: {
    BaseLayout: () => import('@/components/layout/BaseLayout.vue'),
    PasswordRecoverSmsPin: () => import('@/modules/users/views/components/PasswordRecoverSmsPin.vue'),
    PasswordRecoverSmsFields: () => import('@/modules/users/views/components/PasswordRecoverSmsFields.vue')
  },
  data: () => ({
    label: '',
    cpfCnpj: '',
    phoneConfirm: '',
    phoneConfirmError: '',
    btnLoading: false,
    showPinScreen: false,
    wrongPin: false,
    expiredPin: false,
    error: false,
    timer: 0,
    interval: null
  }),
  computed: {
    hasEmail() {
      return this.$store.getters.HIDDEN_EMAIL
    },
    hiddenPhone() {
      return this.$store.getters.HIDDEN_PHONE
    },
    getAssets() {
      return this.$store.getters.GET_ASSETS
    },
    cooldown() {
      return this.$store.getters.COOLDOWN
    },
    messageSent() {
      return this.$store.getters.MESSAGE_SENT
    },
    isTabletOrMobile() {
      return this.$vuetify.breakpoint.width <= 1024
    },
    buttonText() {
      return this.isTabletOrMobile ? 'Reenviar em' : 'Reenviar o código em'
    },
    secondsText() {
      return this.timer > 1 ? this.timer + ' segundos' : this.timer + ' segundo'
    },
    pinMessage() {
      if (this.wrongPin) return 'Código incorreto'
      if (this.expiredPin) return 'Código expirado'
      return 'O código irá expirar em 30 minutos'
    }
  },
  created() {
    const query = Object.entries(this.$route.query)
    this.label = query[0][0].toUpperCase()
    this.cpfCnpj = query[0][1]
  },
  methods: {
    goBack() {
      if (this.showPinScreen) {
        this.showPinScreen = false
        this.phoneConfirm = ''
        this.resetValidations()
        this.clearInterval()
        return
      }
      if (this.hasEmail) {
        this.$router.push({ name: 'recuperar-senha', query: { [this.label]: this.cpfCnpj } })
        return
      }
      this.$router.push({ name: 'cliente-cadastrado', query: { [this.label]: this.cpfCnpj }, params: { fromRecover: true } })
    },
    submitPhoneNumber() {
      this.resetValidations()
      if (!this.phoneConfirm) {
        this.phoneConfirmError = 'Este campo é obrigatório.'
        return
      }
      if (this.phoneConfirm.length < 16) {
        this.phoneConfirmError = 'Insira um telefone com 9 dígitos.'
        return
      }
      this.btnLoading = true
      // CPF stays purely numeric because sanitizeCnpj only strips formatting (so 11 digits remain 11 digits), while CNPJ may be alphanumeric in positions 1-12 and is uppercased before sending.
      // eslint-disable-next-line
      const document_no = sanitizeCnpj(this.cpfCnpj)
      const phone = '55' + this.phoneConfirm.match(/\d/g).join('')
      this.$store
        .dispatch('recoverPasswordSms', { phone, document_no })
        .then(() => {
          this.showPinScreen = true
          if (!this.messageSent) {
            this.$snackbar({
              message: 'Um código já foi enviado para esse número nos últimos 60 segundos.',
              snackbarColor: 'error'
            })
          }
          this.handleTimer()
        })
        .catch(err => {
          if (err.response && err.response.errors && err.response.errors[0] && err.response.errors[0].message === 'User not found') {
            this.phoneConfirmError = 'Número de telefone incorreto'
            return
          }
          this.$snackbar({
            message: 'Ocorreu um erro. Tente novamente mais tarde',
            snackbarColor: 'error'
          })
          console.error(err)
        })
        .finally(() => {
          this.btnLoading = false
        })
    },
    handlePin(code) {
      this.resetValidations()
      if (code.length < 6) return
      this.btnLoading = true
      this.$store
        .dispatch('validatePin', { code })
        .then(() => {
          this.$router.push({ name: 'nova-senha' }).catch(() => {})
        })
        .catch(err => {
          this.error = true
          if (err.response.errors[0].message === 'invalid.phone.validation.code') this.wrongPin = true
          if (err.response.errors[0].message === 'expired.phone.validation.code') this.expiredPin = true
        })
        .finally(() => {
          this.btnLoading = false
        })
    },

    resetValidations() {
      this.wrongPin = false
      this.expiredPin = false
      this.error = false
    },

    clearInterval() {
      clearInterval(this.interval)
      this.interval = null
    },

    handleTimer() {
      this.timer = this.cooldown
      this.interval = setInterval(() => {
        this.timer--
        if (this.timer === 0) this.clearInterval()
      }, 1000)
    },
    beforeDestroy() {
      this.clearInterval()
    }
  }
}
</script>

<style lang="scss">
$phone-size: 600px;
$tablet-size: 960px;

.sms-recover {
  padding: 40px 30px !important;
  text-align: center;

  &-title {
    font-size: 20px;
    font-weight: 500;
    margin-bottom: 17px;
  }

  &-subtitle {
    font-size: 14px;
    max-width: 410px;
    color: #6b7580;
  }

  &-number {
    font-weight: 700;
  }

  .link {
    margin: auto;
    font-size: 14px;
    color: #0079f2;
    cursor: pointer;
  }

  .link:hover {
    text-decoration: underline;
  }

  &-pin-screen {
    max-width: 410px;
    text-align: center;
  }
}

.pin-subtitle {
  width: 410px;
  text-align: center;
  font-weight: 400;
  color: #78838e;
  font-size: 14px;
  display: block;
}

.pin-subtite-error {
  color: #e03210;
}

@media screen and (min-width: $phone-size) {
  .sms-recover {
    text-align: left;

    &-title {
      font-size: 32px;
      margin-bottom: 8px !important;
    }

    &-subtitle {
      margin: 0 0 21px !important;
    }

    &-number {
      font-size: 24px;
      margin-bottom: 8px !important;
    }

    &-break {
      display: block;
    }
  }
}

@media screen and (max-width: $tablet-size) {
  .pin-subtitle {
    width: 100%;
  }

  .sms-recover-pin-screen {
    max-width: 100%;
    text-align: center;
  }
}
</style>
